/**
 * Publish Modal Component
 * 
 * Modal for publishing a new form version with optional changelog.
 */

import { useState } from 'react';
import { X, Upload, Check, AlertCircle, Copy, Loader2 } from 'lucide-react';
import { useFormBuilder } from '../../contexts';
import { formsApi } from '../../lib/api';
import './PublishModal.css';

interface PublishModalProps {
  onClose: () => void;
}

export function PublishModal({ onClose }: PublishModalProps) {
  const { state, publishForm } = useFormBuilder();
  const { form } = state;
  const [changelog, setChangelog] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [embedCode, setEmbedCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handlePublish = async () => {
    if (!form) return;
    
    setIsPublishing(true);
    setError(null);
    
    try {
      await publishForm(changelog || undefined);
      
      // Get embed code after publishing
      try {
        const embedData = await formsApi.getEmbedCode(form.id);
        setEmbedCode(embedData.full_snippet);
      } catch {
        // Embed code is optional
      }
      
      setIsPublished(true);
    } catch (err) {
      setError('Failed to publish form. Please try again.');
      setIsPublishing(false);
    }
  };

  const handleCopy = async () => {
    if (!embedCode) return;
    
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = embedCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isPublishing) {
      onClose();
    }
  };

  // Count steps and fields for summary
  const steps = form?.active_version?.steps || [];
  const totalFields = steps.reduce(
    (acc, step) => acc + (step.variants[0]?.fields.length || 0),
    0
  );
  const hasPhiFields = steps.some((step) =>
    step.variants.some((variant) =>
      variant.fields.some((field) => field.is_phi)
    )
  );

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="publish-modal">
        <div className="modal-header">
          <h2>{isPublished ? 'Published!' : 'Publish Form'}</h2>
          <button
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isPublishing}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {isPublished ? (
            <div className="publish-success">
              <div className="success-icon">
                <Check size={32} />
              </div>
              <h3>Your form is now live!</h3>
              <p>
                Version {(form?.active_version?.version_number || 0) + 1} has been
                published successfully.
              </p>

              {embedCode && (
                <div className="embed-code-section">
                  <label>Embed Code</label>
                  <div className="embed-code-container">
                    <pre>{embedCode}</pre>
                    <button
                      className="btn btn-sm btn-ghost copy-btn"
                      onClick={handleCopy}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="embed-hint">
                    Add this code to your website where you want the form to appear.
                  </p>
                </div>
              )}

              <div className="success-actions">
                <button className="btn btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="publish-summary">
                <h3>Form Summary</h3>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-value">{steps.length}</span>
                    <span className="stat-label">Steps</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{totalFields}</span>
                    <span className="stat-label">Fields</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">
                      v{(form?.active_version?.version_number || 0) + 1}
                    </span>
                    <span className="stat-label">Version</span>
                  </div>
                </div>

                {hasPhiFields && (
                  <div className="phi-warning">
                    <AlertCircle size={16} />
                    <span>
                      This form contains PHI fields. Data will be encrypted at rest.
                    </span>
                  </div>
                )}
              </div>

              <div className="changelog-section">
                <label htmlFor="changelog">
                  Changelog <span className="optional">(optional)</span>
                </label>
                <textarea
                  id="changelog"
                  value={changelog}
                  onChange={(e) => setChangelog(e.target.value)}
                  placeholder="Describe what changed in this version..."
                  rows={3}
                />
              </div>

              {error && (
                <div className="publish-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <div className="publish-actions">
                <button
                  className="btn btn-ghost"
                  onClick={onClose}
                  disabled={isPublishing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handlePublish}
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Publish Now
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublishModal;
